import React from 'react'
import fp from 'lodash/fp'
import Scrollbars from 'react-custom-scrollbars-2'
import {useTranslations} from 'next-intl'
import dynamic from 'next/dynamic'
import {css, useTheme} from '@emotion/react'
import {Divider, List, ListItem, ListItemText, Paper, TextField, Tooltip} from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ArchiveIcon from '@mui/icons-material/Archive'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import ShuffleIcon from '@mui/icons-material/Shuffle'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'

import {useAppCtx} from '../../../../contexts/appCtx'
import downloadFile from '../../../../utils/downloadFile'
import IconButton from '../../../../components/elements/IconButton'
import ClearOptionsDialog from './ClearOptionsDialog'
import {generateRandomOption, reorderList} from './utils'
import UploadOptionsDialog from './UploadOptionsDialog'
import OptionsCollectionDialog from './OptionsCollectionDialog'

const ColorDialog = dynamic(() => import('../../../../components/ColorDialog'), {
  ssr: false,
})

const MaxOptionCount = 20

export default function ControlPanel({
                                       readOnly,
                                       defaultOptions,
                                       options,
                                       onOptionsChange,
                                       setOverrideActiveTab,
                                       ...props
                                     }) {
  const theme = useTheme()
  const styles = {
    root: css`
      height: 100%;

      padding: 24px;
      display: flex;
      flex-direction: column;
    `,

    title: css`
      font-size: 1.5rem;
      font-weight: 700;
    `,

    dropZone: css`
      background-color: #ccc;
      transition-property: height, padding;
      transition-duration: 250ms;
      transition-timing-function: cubic-bezier(0.075, 0.82, 0.165, 1);
      overflow: hidden;
    `,

    hidden: css`
      height: 0;
      padding: 0;
    `,
  }

  const t = useTranslations()

  const [dragged, setDragged] = React.useState(null)
  const [mouse, setMouse] = React.useState([0, 0])
  const [closestDropZone, setClosestDropZone] = React.useState(0)

  React.useEffect(() => {
    const handler = (e) => {
      if (dragged !== null) {
        e.preventDefault()
        setDragged(null)
        onOptionsChange((items) => reorderList(items, dragged, closestDropZone))
      }
    }
    document.addEventListener('mouseup', handler)
    return () => document.removeEventListener('mouseup', handler)
  })

  React.useEffect(() => {
    const handler = (e) => {
      setMouse([e.x, e.y])
    }
    document.addEventListener('mousemove', handler)
    return () => document.removeEventListener('mousemove', handler)
  }, [])

  React.useEffect(() => {
    if (dragged !== null) {
      const elements = Array.from(document.getElementsByClassName('drop-zone'))
      const positions = elements.map((e) => e.getBoundingClientRect().top)
      const absDifferences = positions.map((v) => Math.abs(v - mouse[1]))

      let result = absDifferences.indexOf(Math.min(...absDifferences))
      if (result > dragged) {
        result += 1
      }
      setClosestDropZone(result)
    }
  }, [dragged, mouse])

  const [editMode, setEditMode] = React.useState(false)

  React.useEffect(() => {
    if (editMode) {
      setOverrideActiveTab('left-panel')
    }
  }, [editMode])

  React.useEffect(() => {
    if (readOnly && editMode) {
      setEditMode(false)
    }
  }, [readOnly])

  const onEditButtonClick = () => {
    setEditMode(true)
    setOverrideActiveTab('right-panel')
  }

  const onConfirmButtonClick = () => {
    setEditMode(false)
    setOverrideActiveTab('right-panel')
  }

  const onAddButtonClick = React.useCallback(() => {
    onOptionsChange(options => [
      ...options,
      generateRandomOption(''),
    ])
  }, [onOptionsChange])

  const onRemoveButtonClick = React.useCallback((id) => {
    onOptionsChange(options => options.filter(candidate => candidate.id !== id))
  }, [onOptionsChange])

  const change = React.useCallback((id, newCandidate) => {
    onOptionsChange(candidates => candidates.map(candidate => candidate.id === id ? newCandidate : candidate))
  }, [onOptionsChange])


  const [uploadOptionsDialogOpen, setUploadOptionsDialogOpen] = React.useState(false)
  const onUploadOptionsDialogOpen = () => {
    setUploadOptionsDialogOpen(true)
  }

  const onUploadOptionsDialogClose = () => {
    setUploadOptionsDialogOpen(false)
  }

  const downloadOptions = () => {
    const data = options
      .map(option => option.name)
      .join('\n')

    downloadFile({
      fileName: t('draw-lots.options') + '.txt',
      data: data,
    })
  }

  const [clearOptionsDialogOpen, setClearOptionsDialogOpen] = React.useState(false)
  const onClearOptionsDialogOpen = () => {
    setClearOptionsDialogOpen(true)
  }

  const onClearOptionsDialogClose = () => {
    setClearOptionsDialogOpen(false)
  }

  const shuffleOptions = () => {
    onOptionsChange(options => fp.shuffle(options))
  }

  const [optionsCollectionDialogOpen, setOptionsCollectionDialogOpen] = React.useState(false)
  const onOptionsCollectionDialogOpen = () => {
    setOptionsCollectionDialogOpen(true)
  }

  const onOptionsCollectionDialogClose = () => {
    setOptionsCollectionDialogOpen(false)
  }

  return (
    <>
      <Paper css={styles.root} {...props}>
        <div css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}>
          <div css={styles.title}>{t('draw-lots.options')}</div>
          <div
            css={css`
              margin-left: 16px;

              display: flex;
              align-items: center;
              gap: 4px;

              overflow-x: auto;
            `}
          >
            <IconButton
              tooltip={t('draw-lots.select-option-collections')}
              disabled={readOnly}
              onClick={onOptionsCollectionDialogOpen}
            >
              <LibraryBooksIcon/>
            </IconButton>
            <Divider orientation="vertical" variant="middle" flexItem/>
            <IconButton
              tooltip={t('draw-lots.shuffle-options')}
              disabled={readOnly}
              onClick={shuffleOptions}
            >
              <ShuffleIcon/>
            </IconButton>
            <IconButton
              tooltip={t('draw-lots.clear-options')}
              disabled={readOnly}
              onClick={onClearOptionsDialogOpen}
            >
              <CleaningServicesIcon/>
            </IconButton>
            <Divider orientation="vertical" variant="middle" flexItem/>

            {editMode ? (
              <IconButton
                css={css`
                  color: ${theme.palette.success.main};
                `}
                tooltip={t('draw-lots.confirm')}
                disabled={readOnly}
                onClick={onConfirmButtonClick}
              >
                <CheckIcon/>
              </IconButton>
            ) : (
              <IconButton
                tooltip={t('draw-lots.edit-options')}
                disabled={readOnly}
                onClick={onEditButtonClick}
              >
                <EditIcon/>
              </IconButton>
            )}
          </div>
        </div>
        <Scrollbars universal autoHide>
          <List>
            {options.map((candidate, i) => (
              dragged !== i && (
                <>
                  <OptionItem
                    key={candidate.id}
                    editMode={editMode}
                    setEditMode={setEditMode}
                    candidate={candidate}
                    onChange={change}
                    onDelete={options.length > 1 ? () => onRemoveButtonClick(candidate.id) : null}
                    onDrag={(e) => {
                      e.preventDefault()
                      setDragged(i)
                      setClosestDropZone(i)
                    }}
                  />

                  <OptionItem
                    key={`${candidate.id}-drop-zone`}
                    css={[
                      css`
                        background: #ccc;
                      `,
                      styles.dropZone,
                      dragged === null || closestDropZone !== i + 1 ? styles.hidden : null,
                    ]}
                    className={'drop-zone'}
                    onMouseUp={(e) => {
                      e.preventDefault()
                      if (dragged !== null) {
                        setDragged(null)
                      }
                    }}
                  />
                </>
              )
            ))}
          </List>

          <div css={css`
            display: flex;
            justify-content: space-between;
          `}>
            <div>
              <IconButton
                tooltip={t('draw-lots.add-option')}
                disabled={readOnly || options.length >= MaxOptionCount}
                onClick={onAddButtonClick}
              >
                <AddIcon/>
              </IconButton>
            </div>

            <div>
              <IconButton
                tooltip={t('draw-lots.upload-option-list')}
                disabled={readOnly}
                onClick={onUploadOptionsDialogOpen}
              >
                <UnarchiveIcon/>
              </IconButton>
              <IconButton
                size="small"
                tooltip={t('draw-lots.download-option-list')}
                disabled={readOnly}
                onClick={downloadOptions}
              >
                <ArchiveIcon/>
              </IconButton>
            </div>
          </div>
        </Scrollbars>
        {dragged !== null && (
          <OptionItem
            css={css`
              position: fixed;
              width: 400px;
            `}
            style={{
              left: `${mouse[0] - 16}px`,
              top: `${mouse[1] - 28}px`
            }}
            candidate={options[dragged]}
          />
        )}
      </Paper>

      <ClearOptionsDialog
        open={clearOptionsDialogOpen}
        onClose={onClearOptionsDialogClose}
        defaultOptions={defaultOptions}
        onOptionsChange={onOptionsChange}/>

      <UploadOptionsDialog
        open={uploadOptionsDialogOpen}
        onClose={onUploadOptionsDialogClose}
        onOptionsChange={onOptionsChange}/>

      <OptionsCollectionDialog
        open={optionsCollectionDialogOpen}
        onClose={onOptionsCollectionDialogClose}
        onOptionsChange={onOptionsChange}/>
    </>
  )
}

function OptionItem({
                      readOnly,
                      editMode,
                      setEditMode,
                      onDrag,
                      onChange,
                      onDelete,
                      candidate,
                      ...props
                    }) {
  const t = useTranslations()
  const appCtx = useAppCtx()

  const onWeightChange = React.useCallback((e) => {
    const weight = +e.target.value
    if (weight > 0) {
      onChange(candidate.id, {
        ...candidate,
        weight: weight,
      })
    }
  }, [onChange, candidate])

  const [colorDialogOpen, setColorDialogOpen] = React.useState(false)
  const onColorDialogOpen = () => {
    if (!readOnly) {
      setColorDialogOpen(true)
    }
  }
  const onColorDialogClose = () => {
    setColorDialogOpen(false)
  }

  const onColorChange = (color) => {
    onChange(candidate.id, {
      ...candidate,
      color: color,
    })
  }

  const onNameChange = (e) => {
    onChange(candidate.id, {
      ...candidate,
      name: e.target.value,
    })
  }

  const onEnterPress = (event) => {
    if (event.key === 'Enter' && editMode) {
      setEditMode(false)
    }
  }

  return (
    <>
      <ListItem
        css={css`
          margin-top: 2px;
          height: 56px;
          gap: 8px;
        `}
        component={Paper}
        onClick={editMode ? null : setEditMode}
        onKeyDown={onEnterPress}

        {...props}
      >
        {candidate && (
          <>
            {!appCtx.isMobile && editMode && (
              <IconButton
                css={css`
                  margin-left: -16px;
                  cursor: move;
                `}
                disabled={readOnly}
                disableRipple
                edge="start"
                size="small"
              >
                <DragIndicatorIcon onMouseDown={onDrag}/>
              </IconButton>
            )}

            <Tooltip title={t('draw-lots.modify-option-color')}>
              <div
                css={css`
                  min-width: 32px;
                  min-height: 32px;
                  width: 32px;
                  height: 32px;
                  background: ${candidate.color};
                  cursor: pointer;
                `}
                onClick={onColorDialogOpen}
              />
            </Tooltip>

            {editMode ? (
              <TextField
                disabled={readOnly}
                size="small"
                variant="outlined"
                value={candidate.name}
                onChange={onNameChange}
              />
            ) : (
              <ListItemText>
                {candidate.name}
              </ListItemText>
            )}

            {!appCtx.isMobile && editMode && (
              <TextField
                css={css`
                  width: 70px;
                  min-width: 60px;
                `}
                disabled={readOnly}
                size="small"
                variant="outlined"
                label={t('draw-lots.weight')}
                type="number"
                value={candidate.weight}
                onChange={onWeightChange}
              />
            )}

            {editMode && (
              <IconButton size="small" disabled={readOnly || !onDelete} onClick={onDelete} edge="end">
                <DeleteIcon/>
              </IconButton>
            )}
          </>
        )}
      </ListItem>

      <ColorDialog
        open={colorDialogOpen}
        onClose={onColorDialogClose}
        onConfirm={onColorChange}
        initColor={candidate ? candidate.color : 'white'}
      />
    </>
  )
}

